/**
 * frontend/src/pages/Partners.jsx
 * =================================
 * Partner CRM — list, add, edit, deactivate.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { partnersApi } from '../lib/api'

const PARTNER_TYPES = ['hotel', 'concierge', 'limo', 'airbnb', 'travel_agent', 'other']
const TYPE_LABELS = {
  hotel: 'Hotel',
  concierge: 'Concierge',
  limo: 'Limo / Transportation',
  airbnb: 'Airbnb / Vacation Rental',
  travel_agent: 'Travel Agent',
  other: 'Other',
}
const TYPE_EMOJI = {
  hotel: '🏨',
  concierge: '🎩',
  limo: '🚗',
  airbnb: '🏡',
  travel_agent: '✈️',
  other: '🤝',
}

// ─── Add / Edit Modal ─────────────────────────────────────────────────────

const EMPTY_FORM = {
  name: '',
  type: 'hotel',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  website: '',
  address: '',
  notes: '',
  commission_rate: '',
}

function PartnerModal({ partner, onClose, onSave }) {
  const [form, setForm] = useState(
    partner
      ? {
          ...partner,
          commission_rate: partner.commission_rate
            ? String(Math.round(partner.commission_rate * 100))
            : '',
        }
      : EMPTY_FORM
  )
  const [error, setError] = useState(null)

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    const payload = { ...form }
    if (payload.commission_rate !== '') {
      payload.commission_rate = parseFloat(payload.commission_rate) / 100
    } else {
      delete payload.commission_rate
    }
    // Remove empty strings
    Object.keys(payload).forEach(
      (k) => payload[k] === '' && delete payload[k]
    )

    try {
      await onSave(payload)
      onClose()
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed. Try again.')
    }
  }

  const inputCls =
    'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500'

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <h2 className="font-semibold text-white">
            {partner ? 'Edit Partner' : 'Add Partner'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-white text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && (
            <div className="bg-red-900/40 border border-red-800 text-red-300 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-400 mb-1">
              Partner Name *
            </label>
            <input
              required
              className={inputCls}
              value={form.name}
              onChange={set('name')}
              placeholder="Napa Valley Inn"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Type *</label>
            <select
              className={inputCls}
              value={form.type}
              onChange={set('type')}
            >
              {PARTNER_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Contact Name
              </label>
              <input
                className={inputCls}
                value={form.contact_name}
                onChange={set('contact_name')}
                placeholder="Jane Smith"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Commission %
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                className={inputCls}
                value={form.commission_rate}
                onChange={set('commission_rate')}
                placeholder="10"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Email</label>
              <input
                type="email"
                className={inputCls}
                value={form.contact_email}
                onChange={set('contact_email')}
                placeholder="jane@inn.com"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Phone</label>
              <input
                className={inputCls}
                value={form.contact_phone}
                onChange={set('contact_phone')}
                placeholder="(707) 555-0100"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Website</label>
            <input
              className={inputCls}
              value={form.website}
              onChange={set('website')}
              placeholder="https://napavalleyinn.com"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Notes</label>
            <textarea
              rows={3}
              className={inputCls + ' resize-none'}
              value={form.notes}
              onChange={set('notes')}
              placeholder="VIP relationship, sends 5–10 groups per month…"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              className="flex-1 bg-violet-700 hover:bg-violet-600 text-white text-sm font-medium rounded-lg py-2.5 transition-colors"
            >
              {partner ? 'Save Changes' : 'Add Partner'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Partners page ────────────────────────────────────────────────────────

export default function Partners() {
  const qc = useQueryClient()
  const [modal, setModal] = useState(null) // null | 'add' | partner object
  const [filterType, setFilterType] = useState('all')

  const { data: partners = [], isLoading } = useQuery({
    queryKey: ['partners'],
    queryFn: () => partnersApi.list({ active_only: false }).then((r) => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (data) => partnersApi.create(data),
    onSuccess: () => qc.invalidateQueries(['partners']),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => partnersApi.update(id, data),
    onSuccess: () => qc.invalidateQueries(['partners']),
  })

  const deactivateMutation = useMutation({
    mutationFn: (id) => partnersApi.deactivate(id),
    onSuccess: () => qc.invalidateQueries(['partners']),
  })

  const handleSave = async (data) => {
    if (modal && modal !== 'add') {
      await updateMutation.mutateAsync({ id: modal.id, data })
    } else {
      await createMutation.mutateAsync(data)
    }
  }

  const filtered =
    filterType === 'all'
      ? partners
      : partners.filter((p) => p.type === filterType)

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-white">Partners</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {partners.filter((p) => p.is_active).length} active referral partners
          </p>
        </div>
        <button
          onClick={() => setModal('add')}
          className="px-4 py-2 bg-violet-700 hover:bg-violet-600 text-white text-sm rounded-lg font-medium transition-colors"
        >
          + Add Partner
        </button>
      </div>

      {/* Type filter */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {['all', ...PARTNER_TYPES].map((type) => (
          <button
            key={type}
            onClick={() => setFilterType(type)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              filterType === type
                ? 'bg-violet-700 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            {type === 'all' ? 'All' : TYPE_LABELS[type]}
          </button>
        ))}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-900 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : !filtered.length ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl px-5 py-12 text-center">
          <div className="text-3xl mb-3">🤝</div>
          <p className="text-gray-400 text-sm">No partners yet.</p>
          <button
            onClick={() => setModal('add')}
            className="mt-4 text-violet-400 text-sm hover:underline"
          >
            Add your first partner
          </button>
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-800">
                <th className="px-5 py-3 text-left font-medium">Partner</th>
                <th className="px-4 py-3 text-left font-medium">Contact</th>
                <th className="px-4 py-3 text-left font-medium">Commission</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-gray-800/30 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{TYPE_EMOJI[p.type]}</span>
                      <div>
                        <div className="text-white font-medium">{p.name}</div>
                        <div className="text-xs text-gray-500">
                          {TYPE_LABELS[p.type]}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    <div>{p.contact_name || '—'}</div>
                    {p.contact_email && (
                      <div className="text-xs text-gray-500">{p.contact_email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {p.commission_rate != null
                      ? `${(p.commission_rate * 100).toFixed(1)}%`
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        p.is_active
                          ? 'bg-green-900/50 text-green-400'
                          : 'bg-gray-800 text-gray-500'
                      }`}
                    >
                      {p.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => setModal(p)}
                      className="text-xs text-violet-400 hover:text-violet-300 mr-3"
                    >
                      Edit
                    </button>
                    {p.is_active && (
                      <button
                        onClick={() => {
                          if (confirm(`Deactivate ${p.name}?`))
                            deactivateMutation.mutate(p.id)
                        }}
                        className="text-xs text-red-500 hover:text-red-400"
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <PartnerModal
          partner={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  )
}
